import { useState } from "react";
import { Cpu, Loader2, ToggleLeft, ToggleRight, Sparkles, Settings } from "lucide-react";
import { triggerAI, setInsightDisplayed } from "../lib/api";
import { useClusters } from "../hooks/useClusters";
import { TOOL_ICONS, ACCENT_CLASSES } from "../lib/toolDefaults";
import type { Question, Response, EventTool, ToolAccent } from "../lib/types";

interface Props {
  question: Question;
  responses: Response[];
  tools: EventTool[];
  accessToken: string;
  onAIStatus?: (status: string | null, label: string | null) => Promise<void>;
  onEditTools?: () => void;
}

type AIStatus = "idle" | "loading" | "done" | "error";

const CLUSTER_LABEL = "Cluster werden gebildet…";

export default function AIPanel({
  question,
  responses,
  tools,
  accessToken,
  onAIStatus,
  onEditTools,
}: Props) {
  const { clusters, insights, reload } = useClusters(question.id);
  const [clusterStatus, setClusterStatus] = useState<AIStatus>("idle");
  const [toolStatus, setToolStatus] = useState<Record<string, AIStatus>>({});
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const visibleResponses = responses.filter((r) => !r.is_hidden).map((r) => r.text);
  const insightsByToolId = new Map(insights.map((i) => [i.tool_id ?? "", i]));

  const runCluster = async () => {
    if (visibleResponses.length < 3) {
      setErrorMsg("Mindestens 3 sichtbare Antworten nötig, um zu clustern.");
      return;
    }
    setClusterStatus("loading");
    setErrorMsg(null);
    await onAIStatus?.("cluster", CLUSTER_LABEL);
    try {
      await triggerAI({
        type: "cluster",
        questionId: question.id,
        accessToken,
        triggerReason: clusters.length > 0 ? "manual" : "initial",
      });
      await reload();
      setClusterStatus("done");
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "KI-Anfrage fehlgeschlagen");
      setClusterStatus("error");
    } finally {
      await onAIStatus?.(null, null);
    }
  };

  const runTool = async (tool: EventTool) => {
    setToolStatus((s) => ({ ...s, [tool.id]: "loading" }));
    setErrorMsg(null);
    await onAIStatus?.(tool.tool_key, `${tool.label} wird ausgeführt…`);
    try {
      const result = await triggerAI({
        type: "insight",
        toolId: tool.id,
        force: true, // facilitator-initiated manual run bypasses the cadence gate
        questionId: question.id,
        accessToken,
      });
      await reload();
      setToolStatus((s) => ({ ...s, [tool.id]: result.skipped ? "idle" : "done" }));
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "KI-Anfrage fehlgeschlagen");
      setToolStatus((s) => ({ ...s, [tool.id]: "error" }));
    } finally {
      await onAIStatus?.(null, null);
    }
  };

  const toggleInsightDisplay = async (id: string, current: boolean) => {
    await setInsightDisplayed(id, !current);
    await reload();
  };

  return (
    <div className="flex flex-col gap-5 max-w-2xl">
      {errorMsg && (
        <div className="bg-rc-danger/8 border border-rc-danger/30 rounded-rc-lg px-4 py-3 text-rc-danger text-[14px] font-medium">
          {errorMsg}
        </div>
      )}

      <section className="bg-rc-paper border border-rc-fog rounded-rc-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-rc-lime/25 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-rc-black" />
            </div>
            <div>
              <p className="text-[14px] font-semibold text-rc-black tracking-[-0.01em]">Cluster</p>
              <p className="text-[12px] text-rc-slate">
                {clusters.length > 0 ? `${clusters.length} Cluster` : "Antworten zu Themen gruppieren"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={runCluster}
            disabled={clusterStatus === "loading"}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold bg-rc-berry hover:bg-rc-berry-700 disabled:bg-rc-fog disabled:text-rc-slate disabled:cursor-not-allowed text-rc-paper rounded-pill px-4 py-2 transition-colors active:scale-[0.97]"
          >
            {clusterStatus === "loading" ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Cpu className="w-3.5 h-3.5" />
            )}
            {clusters.length > 0 ? "Neu clustern" : "Clustern"}
          </button>
        </div>

        {clusters.length > 0 && (
          <>
            <ul className="flex flex-col gap-1.5">
              {clusters.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between bg-rc-mist rounded-xl px-3.5 py-2.5 border border-rc-fog"
                >
                  <span className="text-[14px] text-rc-black font-semibold">{c.label}</span>
                  <span className="text-[11px] text-rc-charcoal font-semibold bg-rc-paper border border-rc-fog rounded-pill px-2 py-0.5 tabular-nums">
                    {c.size}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-rc-slate mt-3 leading-[1.45]">
              Cluster aktualisieren sich, sobald genügend neue Antworten ohne klare Zuordnung
              eingegangen sind. Du kannst die Cluster jederzeit fixieren oder manuell neu berechnen.
            </p>
          </>
        )}
      </section>

      <section className="bg-rc-paper border border-rc-fog rounded-rc-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-rc-berry/12 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-rc-berry" />
            </div>
            <div>
              <p className="text-[14px] font-semibold text-rc-black tracking-[-0.01em]">Insights</p>
              <p className="text-[12px] text-rc-slate">Was auffällt</p>
            </div>
          </div>
          {onEditTools && (
            <button
              type="button"
              onClick={onEditTools}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-rc-charcoal hover:text-rc-black bg-rc-cream hover:bg-rc-mist border border-rc-fog rounded-pill px-3 py-1.5 transition-colors"
            >
              <Settings className="w-3 h-3" />
              Tools bearbeiten
            </button>
          )}
        </div>

        {tools.length === 0 ? (
          <p className="text-[13px] text-rc-slate italic">
            Noch keine Tools konfiguriert. In den Event-Einstellungen anlegen.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-2 mb-4">
            {tools.filter((t) => t.is_enabled).map((tool) => {
              const Icon = TOOL_ICONS[tool.icon] ?? Sparkles;
              const status = toolStatus[tool.id] ?? "idle";
              const loading = status === "loading";
              const accent: ToolAccent = (tool.accent as ToolAccent) ?? "paper";
              const accentClasses = ACCENT_CLASSES[accent];
              return (
                <button
                  type="button"
                  key={tool.id}
                  onClick={() => runTool(tool)}
                  disabled={loading}
                  className="flex items-center gap-3 text-[14px] font-semibold bg-rc-cream hover:bg-rc-mist border border-rc-fog hover:border-rc-charcoal/25 disabled:opacity-60 disabled:cursor-not-allowed text-rc-black px-4 py-3 rounded-xl transition-colors text-left"
                >
                  <span className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${accentClasses.swatch}`}>
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
                  </span>
                  <span className="flex-1">{tool.label}</span>
                  {tool.scope === "event" && (
                    <span className="text-[9.5px] font-semibold text-rc-berry uppercase tracking-[0.14em] bg-rc-berry/8 border border-rc-berry/30 rounded-pill px-1.5 py-0.5 flex-shrink-0">
                      Event
                    </span>
                  )}
                  <span className="text-[10px] text-rc-slate font-semibold tracking-[0.14em] uppercase">
                    {loading ? "Läuft…" : "Starten"}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {insights.length > 0 && (
          <div className="flex flex-col gap-2 border-t border-rc-fog pt-4">
            <p className="text-[11px] text-rc-slate font-semibold uppercase tracking-[0.14em] mb-1">
              Aktueller Stand
            </p>
            {tools.filter((t) => insightsByToolId.has(t.id)).map((tool) => {
              const insight = insightsByToolId.get(tool.id);
              if (!insight) return null;
              return (
                <article
                  key={insight.id}
                  className="flex items-start gap-3 bg-rc-cream border border-rc-fog rounded-xl p-3.5"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold text-rc-slate uppercase tracking-[0.14em] mb-1.5">
                      {tool.label}
                    </p>
                    <p className="text-[13.5px] text-rc-black leading-[1.55]">{insight.content}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleInsightDisplay(insight.id, insight.is_displayed)}
                    aria-label={insight.is_displayed ? "Von Bühne entfernen" : "Auf Bühne zeigen"}
                    title={insight.is_displayed ? "Von Bühne entfernen" : "Auf Bühne zeigen"}
                    className={`flex-shrink-0 mt-0.5 transition-colors ${
                      insight.is_displayed ? "text-rc-berry hover:text-rc-berry-700" : "text-rc-slate hover:text-rc-black"
                    }`}
                  >
                    {insight.is_displayed ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
                  </button>
                </article>
              );
            })}
            {insights.filter((i) => !i.tool_id).map((insight) => (
              <article
                key={insight.id}
                className="flex items-start gap-3 bg-rc-cream border border-rc-fog rounded-xl p-3.5"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold text-rc-slate uppercase tracking-[0.14em] mb-1.5">
                    {insight.type ?? "Legacy"}
                  </p>
                  <p className="text-[13.5px] text-rc-black leading-[1.55]">{insight.content}</p>
                </div>
                <button
                  type="button"
                  onClick={() => toggleInsightDisplay(insight.id, insight.is_displayed)}
                  aria-label={insight.is_displayed ? "Von Bühne entfernen" : "Auf Bühne zeigen"}
                  className={`flex-shrink-0 mt-0.5 transition-colors ${
                    insight.is_displayed ? "text-rc-berry hover:text-rc-berry-700" : "text-rc-slate hover:text-rc-black"
                  }`}
                >
                  {insight.is_displayed ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
                </button>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
